// Colorado First Plumbing - Advanced Analytics Tracking
// Comprehensive tracking for plumbing business optimization

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// Event tracking for plumbing business conversions
export const trackEvent = (action: string, category: string, label?: string, value?: number) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
};

// Phone call tracking (critical for plumbing business)
export const trackPhoneCall = (source: string, location?: string) => {
  trackEvent('phone_call', 'contact', `${source}${location ? `_${location}` : ''}`, 1);
  
  // Fire exact Google Ads conversion snippet
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'conversion', {'send_to': 'AW-17180193668/94BoCOChwNkaEITnk4BA'});
  }
};

// Email/form submission tracking  
export const trackFormSubmission = (formType: string, source: string) => {
  trackEvent('form_submission', 'contact', `${formType}_${source}`, 1);
  
  // Fire exact Google Ads conversion snippet
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'conversion', {'send_to': 'AW-17180193668/94BoCOChwNkaEITnk4BA'});
  }
};

// Pricing page views (critical for transparency strategy)
export const trackPricingPageView = (serviceType: string, location?: string) => {
  trackEvent('pricing_view', 'engagement', `${serviceType}${location ? `_${location}` : ''}`, 1);
};

// Service page engagement
export const trackServiceInterest = (service: string, action: string, location?: string) => {
  trackEvent(action, 'service_interest', `${service}${location ? `_${location}` : ''}`, 1);
};

// Emergency service tracking (high-value conversions)
export const trackEmergencyInterest = (action: string, source: string) => {
  trackEvent(action, 'emergency_service', source, 1);
  
  // Emergency calls get conversion tracking
  if (action === 'emergency_call') {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'conversion', {'send_to': 'AW-17180193668/94BoCOChwNkaEITnk4BA'});
    }
  }
};

// Location-specific tracking (critical for local SEO)
export const trackLocationEngagement = (location: string, action: string) => {
  trackEvent(action, 'location_interest', location, 1);
};

// Review and social proof tracking
export const trackSocialProof = (action: string, source: string) => {
  trackEvent(action, 'social_proof', source, 1);
};

// Booking widget interactions
export const trackBookingInteraction = (action: string, source: string) => {
  trackEvent(action, 'booking_widget', source, 1);
  
  if (action === 'booking_completed') {
    // Fire exact Google Ads conversion snippet for bookings
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'conversion', {'send_to': 'AW-17180193668/94BoCOChwNkaEITnk4BA'});
    }
  }
};

// Competitive advantage tracking
export const trackCompetitiveAdvantage = (advantage: string, action: string) => {
  trackEvent(action, 'competitive_advantage', advantage, 1);
};

// Search intent tracking (helps understand customer needs)
export const trackSearchIntent = (query: string, source: string) => {
  trackEvent('internal_search', 'search_intent', `${query}_${source}`, 1);
};

// Video engagement tracking (for project videos)
export const trackVideoEngagement = (videoTitle: string, action: string) => {
  trackEvent(action, 'video_engagement', videoTitle, 1);
};

// Photo gallery engagement
export const trackPhotoEngagement = (service: string, action: string, location?: string) => {
  trackEvent(action, 'photo_engagement', `${service}${location ? `_${location}` : ''}`, 1);
};

// Custom dimensions for advanced segmentation
export const setCustomDimensions = (userId?: string, customerType?: string, serviceArea?: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'G-J6L5Z5R4HE', {
      custom_map: {
        'custom_dimension_1': 'customer_type',
        'custom_dimension_2': 'service_area',
        'custom_dimension_3': 'lead_source'
      }
    });
    
    if (customerType) {
      window.gtag('event', 'page_view', {
        customer_type: customerType,
        service_area: serviceArea
      });
    }
  }
};

// Enhanced phone number click tracking
export const setupPhoneNumberTracking = () => {
  if (typeof window !== 'undefined') {
    // Track all phone number clicks
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      
      // Check if clicked element is a phone link or contains phone number
      if (target.tagName === 'A' && target.getAttribute('href')?.includes('tel:')) {
        const phoneNumber = target.getAttribute('href')?.replace('tel:', '') || 'unknown';
        const source = target.closest('[data-source]')?.getAttribute('data-source') || 'general';
        trackPhoneCall(source);
      }
    });
  }
};

// Enhanced form submission tracking
export const setupFormTracking = () => {
  if (typeof window !== 'undefined') {
    // Track all form submissions
    document.addEventListener('submit', (event) => {
      const form = event.target as HTMLFormElement;
      const formId = form.id || 'unknown_form';
      const source = form.getAttribute('data-source') || 'website';
      
      trackFormSubmission(formId, source);
    });
  }
};

// Track scroll depth (engagement metric)
export const trackScrollDepth = () => {
  if (typeof window !== 'undefined') {
    let maxScroll = 0;
    
    window.addEventListener('scroll', () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100
      );
      
      if (scrollPercent > maxScroll) {
        maxScroll = scrollPercent;
        
        // Track milestone scroll depths
        if ([25, 50, 75, 100].includes(scrollPercent)) {
          trackEvent('scroll_depth', 'engagement', `${scrollPercent}%`, scrollPercent);
        }
      }
    });
  }
};

// Initialize all tracking
export const initializeTracking = () => {
  setupPhoneNumberTracking();
  setupFormTracking();
  trackScrollDepth();
};